import React from "react"
import { useTranslation } from "react-i18next"
import GetEnvironment from "../../environments"
import UserPermissionsModal from "./UserPermissionsModal"
import { useQuery } from "@apollo/client"
import { GET_PROJECT_ACCESS } from "../../api/QuadriApi"
import { useRecoilState, useRecoilValueLoadable } from "recoil"
import { usersByTidUuidState, userGroupsState, addSnackSelector } from "../StateSelectors"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import { SnackProps } from "./SnackBars"
import Oops from "../Oops/Oops"

const PermissionsPanel = (props: any) => {
  const { t } = useTranslation("config")
  const { isEnableButtonHidden, onClose, quadriProjectId, connectProjectId, disabled } = props
  const [showUserPermissionsModal, setShowUserPermissionsModal] = React.useState(false)
  const showModal = () => setShowUserPermissionsModal(true)
  const closeModal = () => window.location.reload()
  const [, addSnack] = useRecoilState(addSnackSelector)

  // Recoil states
  const usersByTid = useRecoilValueLoadable(usersByTidUuidState)
  const groups = useRecoilValueLoadable(userGroupsState)

  const getLoadErrorSnackProps = (t: any, errorMessage?: string): SnackProps => ({
    type: "error",
    message: `${t("userPermissionsModal.loadError")}${errorMessage ? `: ${errorMessage}` : ""}`
  })

  // GraphQL query
  const { data, loading, error } = useQuery(GET_PROJECT_ACCESS, {
    fetchPolicy: "no-cache",
    variables: { quadriProjectId, connectProjectId },
    onError: (error) => addSnack(getLoadErrorSnackProps(t, error.message))
  })

  if (error)
    return (
      <div className="col-3 d-flex flex-column" style={{ height: "100%", marginTop: "5rem" }}>
        <Oops body={error.message} skipImg={true} />
      </div>
    )

  const projectAccess = data?.getProjectAccess
  const defaultAccess = projectAccess?.defaultAccess
  const userOrGroupAccess = projectAccess?.userOrGroupAccess || []

  // Map access level to friendly name
  const formatAccess = (access: string) => {
    switch (access) {
      case "FullAccess":
        return t("userPermissionsModal.fullAccess")
      case "ReadOnly":
        return t("userPermissionsModal.readOnly")
      case "NoAccess":
        return t("userPermissionsModal.noAccess")
      default:
        return access
    }
  }

  const UserAndGroupList = () => {
    if (usersByTid.state === "loading" || groups.state === "loading") {
      return <LoadingSkeleton />
    }

    const getUserName = (tiduuid: string, email: string | null) => {
      const user = usersByTid?.contents?.[tiduuid]
      if (user) {
        const fullName = `${user.firstName} ${user.lastName}`.trim()
        return email ? `${fullName} (${email})` : fullName
      }
      return email || "Unknown User"
    }

    const getGroupName = (id: string) => {
      const groupContents = groups?.contents
      if (!Array.isArray(groupContents)) return "Unknown Group"
      const group = groupContents?.find((g: any) => g.id === id)
      return group ? group.name : "Unknown Group"
    }

    return (
      <div className="d-flex flex-column mt-3">
        {defaultAccess && (
          <div className="mb-2">
            <label style={{ color: "#6a6976", fontSize: "0.875rem" }}>{t("userPermissionsPanel.defaultAccess")}</label>
            <div>{formatAccess(defaultAccess)}</div>
          </div>
        )}

        {userOrGroupAccess.map((entry: any) => (
          <div key={entry.id} className="mb-2">
            <label style={{ color: "#6a6976", fontSize: "0.875rem" }}>
              {entry.type === "User" ? getUserName(entry.id, entry.email) : getGroupName(entry.id)}
            </label>
            <div>{formatAccess(entry.access)}</div>
          </div>
        ))}
      </div>
    )
  }

  const LoadingSkeleton = () => <Skeleton height={"2rem"} count={userOrGroupAccess.length + 1} />

  return (
    <div className="col-3 d-flex">
      <div
        className="mb-2 mt-1 w-100"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0 0 0.25rem #AEAEB6",
          boxSizing: "border-box"
        }}
      >
        {loading && (
          <div className="d-flex h-100 justify-content-center align-items-center">
            <div className="center">
              <i className="icon-font tc-icon-spinner-dark" role="status"></i>
            </div>
          </div>
        )}
        <div className="d-flex flex-column mb-3 p-4">
          <div className="d-flex justify-content-between align-items-center">
            <h3>{t("userPermissionsPanel.title")}</h3>
            <div className="d-flex">
              {GetEnvironment().showUserPermissions && !isEnableButtonHidden && (
                <button
                  className="button icon-circle icon-medium tertiary"
                  aria-label="Edit Permissions"
                  onClick={showModal}
                  disabled={disabled}
                >
                  <i className="icon-font tc-icon-edit"></i>
                </button>
              )}
              {showUserPermissionsModal && <UserPermissionsModal closeModal={closeModal} />}
              <button
                className="button icon-circle icon-medium tertiary"
                aria-label="Close Permissions"
                disabled={isEnableButtonHidden}
                onClick={onClose}
              >
                <i className="icon-font tc-icon-content-close"></i>
              </button>
            </div>
          </div>

          <UserAndGroupList />
        </div>
      </div>
    </div>
  )
}

export default PermissionsPanel
